import { AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../services/hooks/apiRedux';
import { useAuth } from '../store/auth';
import i18n from '../plugins/i18n/i18n';
import { errorToast, successToast } from '../utils/toasts';
import { GetFeesResponse } from '../services/Interfaces/IAccountService';
import { InstancesServices } from '../services/InstancesServices';

type UpdateFeePayload = {
  fee_id: number;
  value: number;
  is_active: 0 | 1;
};

export const useFees = () => {
  const [fees, setFees] = useState<GetFeesResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const api: AxiosInstance = useApi();
  const { token } = useAuth();

  const cacheRef = useRef<{ key: string | null; data: GetFeesResponse[] }>({ key: null, data: [] });
  const promiseRef = useRef<Promise<GetFeesResponse[] | undefined> | null>(null);

  const fetchSummary = useCallback(async (force = false) => {
    if (!token) {
      cacheRef.current = { key: null, data: [] };
      setFees([]);
      setLoading(false);
      return [];
    }

    const key = `${token}|${i18n.language.toLowerCase()}`;

    if (!force) {
      if (cacheRef.current.key === key && cacheRef.current.data.length) {
        setFees(cacheRef.current.data);
        setLoading(false);
        return cacheRef.current.data;
      }

      if (promiseRef.current) {
        const data = await promiseRef.current;
        return data ?? [];
      }
    } else {
      cacheRef.current = { key: null, data: [] };
      promiseRef.current = null;
    }

    setLoading(true);

    const request = InstancesServices.getFees(api, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      }
    })
      .then((response) => {
        const data = response ?? [];
        cacheRef.current = { key, data };
        setFees(data);
        return data;
      })
      .catch((error: any) => {
        console.error('Erro ao buscar resumo de taxas:', error);
        errorToast(error?.response?.data?.message || i18n.t('environment.errorFetchingPlanInfo'));
        cacheRef.current = { key, data: [] };
        setFees([]);
        return [];
      })
      .finally(() => {
        setLoading(false);
        promiseRef.current = null;
      });

    promiseRef.current = request;
    return request;
  }, [api, token, i18n.language]);

  const updateFee = async (payload: UpdateFeePayload) => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Language': i18n.language.toLowerCase()
        }
      };

      await InstancesServices.updateFee(api, payload, config);
      successToast(i18n.t('environment.feesUpdatedSuccess'));
      await fetchSummary(true);
    } catch (error: any) {
      console.error('Erro ao atualizar taxa:', error);
      errorToast(error?.response?.data?.message || i18n.t('environment.errorUpdatingFee'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  return {
    fees,
    loading,
    updateFee,
  };
};
